import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const PricePage = () => {
  const { t, language } = useLanguage();

  // Helper to get status text based on current language
  const getStatusText = (status) => {
    if (status === 'I lager') return t('prices.inStock');
    if (status === 'Beställningsvara') return t('prices.orderItem');
    if (status === 'Säsongvara') return t('prices.seasonal');
    if (status === 'Begränsat') return t('prices.limited');
    return status;
  };

  // Helper to get product name based on current language
  const getProductName = (key) => {
    const translation = t(`prices.products.${key}`);
    return translation || key;
  };

  const products = [
    {
      name: 'carrot',
      nameKey: 'carrot',
      price: '30 kr',
      unit: '1 kg',
      status: 'I lager'
    },
    {
      name: 'carrot',
      nameKey: 'carrot',
      price: '250 kr',
      unit: '10 kg',
      status: 'Beställningsvara'
    },
    {
      name: 'fodderCarrot',
      nameKey: 'fodderCarrot',
      price: '100 kr',
      unit: '10 kg',
      status: 'Beställningsvara'
    },
    {
      name: 'potato',
      nameKey: 'potato',
      price: '39 kr',
      unit: '2 kg',
      status: 'I lager'
    },
    {
      name: 'potato',
      nameKey: 'potato',
      price: '90 kr',
      unit: '5 kg',
      status: 'I lager'
    },
    {
      name: 'potatoDitta',
      nameKey: 'potatoDitta',
      price: '49 kr',
      unit: '2 kg',
      status: 'I lager'
    },
    {
      name: 'almonPotato',
      nameKey: 'almonPotato',
      price: '59 kr',
      unit: '2 kg',
      status: 'I lager'
    },
    {
      name: 'pointCabbage',
      nameKey: 'pointCabbage',
      price: '40 kr',
      unit: 'kg',
      status: 'Säsongvara'
    },
    {
      name: 'whiteCabbage',
      nameKey: 'whiteCabbage',
      price: '40 kr',
      unit: 'kg',
      status: 'Säsongvara'
    },
    {
      name: 'redCabbage',
      nameKey: 'redCabbage',
      price: '30 kr',
      unit: 'kg',
      status: 'Säsongvara'
    },
    {
      name: 'rutabaga',
      nameKey: 'rutabaga',
      price: '20 kr',
      unit: 'kg',
      status: 'Säsongvara'
    },
    {
      name: 'turnip',
      nameKey: 'turnip',
      price: '25 kr',
      unit: 'kg',
      status: 'I lager'
    },
    {
      name: 'fermentedWhiteCabbage',
      nameKey: 'fermentedWhiteCabbage',
      price: '35 kr',
      unit: '460 g',
      status: 'I lager'
    },
    {
      name: 'fermentedWhiteCabbage',
      nameKey: 'fermentedWhiteCabbage',
      price: '60 kr',
      unit: '640 g',
      status: 'I lager'
    },
    {
      name: 'fermentedWhiteCabbageBottle',
      nameKey: 'fermentedWhiteCabbageBottle',
      price: '120 kr',
      unit: '2000 g',
      status: 'I lager'
    },
    {
      name: 'fermentedCarrot',
      nameKey: 'fermentedCarrot',
      price: '35 kr',
      unit: '510 g',
      status: 'I lager'
    },
    {
      name: 'fermentedMixed',
      nameKey: 'fermentedMixed',
      price: '35 kr',
      unit: '460 g',
      status: 'I lager'
    },
    {
      name: 'Rödbetstråån med lakrits',
      nameKey: 'beetsString',
      price: '30 kr',
      unit: '100 g',
      status: 'Begränsat'
    },
    {
      name: 'Morotstråån med havtorn',
      nameKey: 'carrotString',
      price: '30 kr',
      unit: '100 g',
      status: 'Begränsat'
    },
    {
      name: 'grillSauce',
      nameKey: 'grillSauce',
      price: '45 kr',
      unit: '200 ml',
      status: 'Begränsat'
    },
    {
      name: 'carrotSyrup',
      nameKey: 'carrotSyrup',
      price: '70 kr',
      unit: '250 ml',
      status: 'Begränsat'
    },
    {
      name: 'spruceSyrup',
      nameKey: 'spruceSyrup',
      price: '60 kr',
      unit: '100 ml',
      status: 'I lager'
    },
    {
      name: 'beetsKvass',
      nameKey: 'beetsKvass',
      price: '30 kr',
      unit: '250 ml',
      status: 'I lager'
    },
    {
      name: 'carrotJuice',
      nameKey: 'carrotJuice',
      price: '30 kr',
      unit: '250 ml',
      status: 'I lager'
    },
    {
      name: 'vegetableBroth',
      nameKey: 'vegetableBroth',
      price: '40:-',
      unit: '250 ml',
      status: 'I lager'
    }
  ];

  const getAvailabilityColor = (status) => {
    switch (status) {
      case 'I lager':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Beställningsvara':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Säsongvara':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Begränsat':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            {t('prices.title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('prices.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Main Price List */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="md:col-span-2 bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <div className="bg-primary-green p-6">
              <h2 className="text-2xl font-bold text-white">
                {t('prices.inStore')}
              </h2>
              <p className="text-white/80 text-sm mt-2">
                {t('prices.updated')}: {new Date().toLocaleDateString(language === 'sv' ? 'sv-SE' : language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : 'en-US')}
              </p>
            </div>
            
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b-2 border-gray-100">
                    <tr>
                      <th className="text-left py-4 px-4 text-gray-600 font-semibold">{t('products.title')}</th>
                      <th className="text-right py-4 px-4 text-gray-600 font-semibold">{t('prices.priceInfo')}</th>
                      <th className="text-right py-4 px-4 text-gray-600 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product, index) => (
                      <tr
                        key={index}
                        className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                          index === products.length - 1 ? 'border-b-0' : ''
                        }`}
                      >
                        <td className="py-4 px-4">
                          <div className="font-semibold text-gray-800">{getProductName(product.nameKey)}</div>
                          <div className="text-sm text-gray-500">{product.unit}</div>
                        </td>
                        <td className="text-right py-4 px-4 font-bold text-primary-green">
                          {product.price}
                        </td>
                        <td className="text-right py-4 px-4">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${getAvailabilityColor(product.status)}`}>
                            {getStatusText(product.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>

          {/* Sidebar Info */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white rounded-lg shadow-lg p-6"
            >
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                {t('prices.general')}
              </h3>
              <ul className="space-y-3">
                {(t('prices.generalList', { returnObjects: true }) || []).map((item, index) => (
                  <li key={index} className="flex items-start text-gray-600">
                    <span className="mr-2 text-accent-green">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-accent-green/10 rounded-lg shadow-lg p-6 border border-accent-green/20"
            >
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                {t('prices.wholesale')}
              </h3>
              <ul className="space-y-3">
                {(t('prices.wholesaleList', { returnObjects: true }) || []).map((item, index) => (
                  <li key={index} className="flex items-start text-gray-700">
                    <span className="mr-2 text-accent-green">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-yellow-50 rounded-lg shadow-lg p-6 border border-yellow-100"
            >
              <p className="text-sm text-yellow-800 italic">
                {t('prices.note')}
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricePage;